import {jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {MainLayout} from '~/layouts/main';
export const meta = {
  title: 'DIP | В реальной жизни',
  description: 'Как принцип инверсии зависимостей помогает решать задачи из реальной жизни, примеры и описание применения'
};
const MDXLayout = ({children}) => _jsx(MainLayout, {
  meta: meta,
  children: children
});
function _createMdxContent(props) {
  const _components = {
    a: "a",
    code: "code",
    h1: "h1",
    h2: "h2",
    li: "li",
    p: "p",
    pre: "pre",
    section: "section",
    ul: "ul",
    ..._provideComponents(),
    ...props.components
  };
  return _jsxs(_components.section, {
    children: [_jsx(_components.h1, {
      children: "В реальной жизни"
    }), _jsxs(_components.p, {
      children: ["Для большего контроля над зависимостями и упрощения тестирования DIP предлагает использовать ", _jsx(_components.a, {
        href: "https://en.wikipedia.org/wiki/Inversion_of_control",
        children: "инверсию управления"
      }), " (Inversion of Control, IoC) и ", _jsx(_components.a, {
        href: "https://en.wikipedia.org/wiki/Dependency_injection",
        children: "инъекцию зависимостей"
      }), " (Dependency Injection, DI)."]
    }), _jsxs(_components.section, {
      children: [_jsx(_components.h2, {
        children: "DIP, DI и тестирование"
      }), _jsxs(_components.p, {
        children: ["Писать тесты без DIP возможно, но следуя принципу это делать проще. Если модуль зависит от абстракции, то его зависимость легче подменить ", _jsx(_components.a, {
          href: "https://stackoverflow.com/questions/346372/whats-the-difference-between-faking-mocking-and-stubbing",
          children: "фейковым объектом"
        }), "."]
      }), _jsxs(_components.p, {
        children: ["Вернёмся к примеру с аутентификацией. Для того, чтобы протестировать реализацию, которая не следует DIP, нам придётся создать экземпляр класса ", _jsx(_components.code, {
          children: "MySqlConnection"
        }), ":"]
      }), _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "language-ts",
          children: "describe('Auth', () => {\n  let connection: MySqlConnection\n  let auth: Auth\n\n  beforeEach(() => {\n    connection = new MySqlConnection(/*...*/)\n    auth = new Auth(connection)\n  })\n\n  it('should successfully authenticate user', async () => {\n    const result: AuthResult = await auth.authenticate('Alex', '123')\n    expect(result.status).toEqual(200)\n  })\n})\n"
        })
      }), _jsxs(_components.p, {
        children: ["Проблема — в производительности. Хороший модульный тест ", _jsx(_components.a, {
          href: "https://bespoyasov.ru/ttt-tdd/",
          children: "должен быть быстрым"
        }), ", а создавать экземпляр настоящего объекта на каждый тест — ресурсозатратно."]
      }), _jsxs(_components.p, {
        children: ["Кроме того, в плохой реализации класс ", _jsx(_components.code, {
          children: "MySqlConnection"
        }), " сам может зависеть от других модулей, экземпляры которых тоже будут создаваться. Всё это сильно тормозит тесты."]
      }), _jsx(_components.p, {
        children: "Теперь попробуем протестировать реализацию, которая следует DIP:"
      }), _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "language-ts",
          children: "class DbMock implements DataBaseConnection {\n  connect(host: string, user: string, password: string): void {\n    // Здесь дешёвые операции-заглушки,\n    // подключаться к базе совсем не обязательно.\n  }\n\n  // Тут реализация только тех методов,\n  // которые понадобятся для тестов.\n}\n\ndescribe('Auth', () => {\n  let connection: DataBaseConnection\n  let auth: Auth\n\n  beforeEach(() => {\n    connection = new DbMock(/*...*/)\n    auth = new Auth(connection)\n  })\n\n  // ...\n})\n"
        })
      }), _jsxs(_components.p, {
        children: ["Класс ", _jsx(_components.code, {
          children: "DbMock"
        }), " реализует интерфейс ", _jsx(_components.code, {
          children: "DataBaseConnection"
        }), ". Мы можем спроектировать его максимально простым и лёгким, это ускорит работу теста."]
      })]
    }), _jsxs(_components.section, {
      children: [_jsx(_components.h2, {
        children: "Инверсия управления"
      }), _jsxs(_components.p, {
        children: ["DI — это частный случай ", _jsx(_components.a, {
          href: "https://en.wikipedia.org/wiki/Inversion_of_control",
          children: "инверсии управления"
        }), ". При этом подходе контроль за выполнением программы отдаётся фреймворку, который знает, в какой момент и какую функцию надо вызвать. Цель IoC — сделать систему расширяемой."]
      }), _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "http://inversify.io/",
          children: "Inversify"
        }), " предлагает решение для IoC на TypeScript. Inversify предоставляет API для создания контейнеров с указанием зависимостей, которые потом подставляются автоматически."]
      })]
    }), _jsxs(_components.section, {
      children: [_jsx(_components.h2, {
        children: "Материалы к разделу"
      }), _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://en.wikipedia.org/wiki/Dependency_injection",
            children: "Dependency Injection"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://en.wikipedia.org/wiki/Inversion_of_control",
            children: "Inversion of Control"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://stackoverflow.com/questions/346372/whats-the-difference-between-faking-mocking-and-stubbing",
            children: "What's the difference between faking, mocking, and stubbing?"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://www.slideshare.net/paskavatta/mo-dip-15104789",
            children: "Mock Objects, Design and DIP"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://bespoyasov.ru/ttt-tdd/",
            children: "Пример разработки Крестиков‑ноликов по TDD"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://bulldogjob.com/articles/971-on-dependency-injection-loose-coupling-and-unit-tests-in-c",
            children: "On DI, Loose Coupling and Unit Tests"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "http://inversify.io/",
            children: "Inversify.io"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://codeburst.io/understanding-solid-principles-dependency-injection-d570c15560ab",
            children: "Dependency Inversion with Inversify.io"
          })
        }), "\n"]
      })]
    })]
  });
}
export default function MDXContent(props = {}) {
  return _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  });
}
